import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import Table from 'react-bootstrap/Table';
import Collapse from 'react-bootstrap/Collapse';
import { Tooltip } from 'react-tooltip';
import moment from 'moment';
import s from './KioskPasscodeSection.module.scss';
import VerificationCodeInput from '../../WSAControls/VerificationCodeInput/VerificationCodeInput';
import help from '../../../assets/help-grey.svg';
import collapseDown from '../../../assets/collapse-down.svg';
import collapseUp from '../../../assets/collapse-up.svg';
import { getOrganisationManager } from '../../../selectors/index';
import { getKioskPasscodeStatusThunk } from '../../../actions/organisationManager';

// BCC-20: kiosk passcode - set/change section embedded in the organisation
// edit screen (Organisation.jsx), shown only when the caller also has
// KIOSKPASSCODEUPDATE (see Organisation.jsx's gating). No dedicated
// save/cancel controls here - the field is bound straight into
// selectedOrganisation.KioskPasscode (value/onChange passed down from
// Organisation.jsx, same as every other field on this form) and saved via
// the normal "Save Organisation" button. Organisation.jsx's submitForm
// intercepts that submit with a confirmation dialog when this field is
// non-empty, before the save/AC's "requires confirmation" happens.
const KioskPasscodeSection = ({ organisationId, value, onChange, formErrors }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const organisationManager = useSelector(state => getOrganisationManager(state));
  const { IsSet, SetDateTime, SetByUserName, UnitStatuses } = organisationManager.kioskPasscodeStatus;

  // Status (IsSet/SetDateTime/UnitStatuses) all come back from a single
  // GET call - rather than firing it on mount, it's deferred until the
  // user actually expands "Propagation status" below, so opening the
  // Organisation edit screen doesn't pay for it unless it's wanted.
  const [isPropagationOpen, setIsPropagationOpen] = useState(false);
  const [hasRequestedStatus, setHasRequestedStatus] = useState(false);

  const handleTogglePropagation = () => {
    const nextOpen = !isPropagationOpen;
    setIsPropagationOpen(nextOpen);
    if (nextOpen && !hasRequestedStatus && organisationId) {
      dispatch(getKioskPasscodeStatusThunk(organisationId, 'KIOSKPASSCODEUPDATE'));
      setHasRequestedStatus(true);
    }
  };

  const statusBadgeClass = status => {
    switch (status) {
      case 'Delivered':
        return s.delivered;
      case 'Applied':
        return s.applied;
      case 'NotAssigned':
        return s.notAssigned;
      case 'Pending':
      default:
        return s.pending;
    }
  };

  // Applied is the more recent/advanced state when both are set (a unit
  // can't be Applied without having been Delivered first) - show whichever
  // one actually happened. Pending/NotAssigned units have neither.
  const unitUpdatedDateTime = unit => unit.AppliedDateTime || unit.DeliveredDateTime || null;

  const statusLabel = status => {
    switch (status) {
      case 'Delivered':
        return intl.formatMessage({ id: 'organisation.kioskPasscode.status.delivered', defaultMessage: 'Delivered' });
      case 'Applied':
        return intl.formatMessage({ id: 'organisation.kioskPasscode.status.applied', defaultMessage: 'Applied' });
      case 'NotAssigned':
        return intl.formatMessage({ id: 'organisation.kioskPasscode.status.notAssigned', defaultMessage: 'Not assigned' });
      case 'Pending':
      default:
        return intl.formatMessage({ id: 'organisation.kioskPasscode.status.pending', defaultMessage: 'Pending' });
    }
  };

  return (
    <div className={s.kioskPasscode}>
      <h4>
        <FormattedMessage id="organisation.kioskPasscode.heading" defaultMessage="Kiosk Passcode" />
        <img
          src={help}
          alt="info"
          className={s.infoIcon}
          onClick={e => e.preventDefault()}
          data-tooltip-id="kioskPasscodeTooltip"
          data-tooltip-content={intl.formatMessage({
            id: 'organisation.kioskPasscode.tooltip',
            defaultMessage:
              'Kiosks in this organisation use this passcode to unlock servicing mode. Until you set one, units use a default passcode. Changing it applies to every unit in this organisation - a unit only asks for the new code the next time it is unlocked, so an active session is not interrupted.'
          })}
        />
        <Tooltip id="kioskPasscodeTooltip" className="kioskPasscodeTooltip" />
      </h4>

      <div className={s.statusRow} data-unittest="kioskPasscodeStatus">
        {hasRequestedStatus ? (
          IsSet ? (
            <span>
              <FormattedMessage id="organisation.kioskPasscode.statusSet" defaultMessage="A passcode is set." />{' '}
              {SetDateTime && (
                <FormattedMessage
                  id="organisation.kioskPasscode.lastChanged"
                  defaultMessage="Last changed {date} by {user}."
                  values={{ date: moment.unix(SetDateTime).format('DD MMM YYYY, HH:mm'), user: SetByUserName || '-' }}
                />
              )}
            </span>
          ) : (
            <span>
              <FormattedMessage
                id="organisation.kioskPasscode.statusNotSet"
                defaultMessage="No passcode has been set - units are using the default passcode."
              />
            </span>
          )
        ) : (
          <span className={s.statusUnknown}>
            <FormattedMessage
              id="organisation.kioskPasscode.statusUnknown"
              defaultMessage="Expand Propagation status below to check whether a passcode is set."
            />
          </span>
        )}
      </div>

      <div className={s.changeForm}>
        <label>
          <FormattedMessage id="organisation.kioskPasscode.newPasscodeLabel" defaultMessage="New 4-digit passcode" />
        </label>
        <VerificationCodeInput type="number" fields={4} value={value} onChange={onChange} autoComplete="off" />
        {formErrors && formErrors.KioskPasscode && (
          <p role="alert" className={s.error}>
            {formErrors.KioskPasscode}
          </p>
        )}
      </div>

      <div className={s.propagation}>
        <div
          className={s.propagationToggle}
          onClick={handleTogglePropagation}
          role="button"
          tabIndex={0}
          onKeyPress={e => (e.key === 'Enter' ? handleTogglePropagation() : null)}
          aria-controls="kiosk-passcode-propagation-section"
          aria-expanded={isPropagationOpen}
          data-unittest="kioskPasscodePropagationToggle"
        >
          <img src={isPropagationOpen ? collapseDown : collapseUp} alt="Collapse section" className={s.collapseIcon} />
          <h5>
            <FormattedMessage id="organisation.kioskPasscode.propagationHeading" defaultMessage="Propagation status" />
          </h5>
        </div>
        <Collapse in={isPropagationOpen}>
          <div id="kiosk-passcode-propagation-section">
            {!hasRequestedStatus ? null : UnitStatuses && UnitStatuses.length > 0 ? (
              <Table responsive>
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage id="organisation.kioskPasscode.unitColumn" defaultMessage="Unit" />
                    </th>
                    <th>
                      <FormattedMessage id="organisation.kioskPasscode.statusColumn" defaultMessage="Status" />
                    </th>
                    <th>
                      <FormattedMessage id="organisation.kioskPasscode.updatedColumn" defaultMessage="Updated" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {UnitStatuses.map(unit => {
                    const updatedDateTime = unitUpdatedDateTime(unit);
                    return (
                      <tr key={unit.UnitId}>
                        <td>{unit.UnitName || unit.UnitNumber || unit.UnitId}</td>
                        <td>
                          <span className={`${s.statusBadge} ${statusBadgeClass(unit.Status)}`}>{statusLabel(unit.Status)}</span>
                        </td>
                        <td>{updatedDateTime ? moment.unix(updatedDateTime).format('DD MMM YYYY, HH:mm') : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <p className={s.emptyState}>
                <FormattedMessage id="organisation.kioskPasscode.propagationEmpty" defaultMessage="This organisation has no units yet." />
              </p>
            )}
          </div>
        </Collapse>
      </div>
    </div>
  );
};

KioskPasscodeSection.propTypes = {
  organisationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  formErrors: PropTypes.object
};

export default KioskPasscodeSection;
